import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';

const logo = require('./logo.png');
import { icons } from './icons';
import { labels } from './labels';

// These are filled by functions's below
let categories = [];
let cities = [];
let cityToUse;
let map;
let visiblePoints = [];

const splittedLocation = location.href.split('?')[1]?.split('&');
const urlQueryVariables = splittedLocation?.reduce((prev, cur) => {
  const [key, value] = cur.split('=');
  return { ...prev, [key]: decodeURIComponent(value) };
}, {});

const config = {
  accessToken: 'pk.eyJ1IjoiZGVubmlzbGlua3UiLCJhIjoiY2t6ZmFleGNkMnIzZDJ2bnhiemRxenV4OSJ9.TcewaJ42NJX-j35jMiCTkQ',
  city: urlQueryVariables?.city,
  hotspotCityEndpoint:
    urlQueryVariables?.hotspotCityEndpoint ?? 'https://followfox.nl/wp-json/wp/v2/hotspot-city?per_page=100',
  hotspotEndpoint: urlQueryVariables?.hotspotEndpoint ?? 'https://followfox.nl/wp-json/wp/v2/hotspot?per_page=100',
  hotspotTypeEndpoint:
    urlQueryVariables?.hotspotTypeEndpoint ?? 'https://followfox.nl/wp-json/wp/v2/hotspot-type?per_page=100',
  mapId: 'followFoxMap',
};

initializeMap();

async function initializeMap() {
  await Promise.all([fetchHotspotCities(), fetchHotspotCategories()]);

  cityToUse =
    cities.find(({ key }) => key === `key-${config.city}`) ||
    cities.find(({ label }) => label?.toUpperCase().includes('Nijmegen'.toUpperCase()));

  const isArnhem = cityToUse.label?.toUpperCase().includes('Arnhem'.toUpperCase());

  /* 
  Use this tool to find coordinates.
  Note, they are lat lng, and we need lng lat!
  https://docs.mapbox.com/playground/geocoding/
*/

  const nijmegenCenter = [5.860885891852178, 51.84323681618659];
  const nijmegenMaxBounds = [
    [5.655837770121423, 51.73923820062021], // Southwest coordinates
    [5.996415163367715, 51.887617938591916], // Northeast coordinates
  ];

  const arnhemCenter = [5.908945967152874, 51.98117289950329];
  const arnhemMaxBounds = [
    [5.7820892, 51.9458731], // Southwest coordinates
    [6.012111425792085, 52.1058726], // Northeast coordinates
  ];

  const center = isArnhem ? arnhemCenter : nijmegenCenter;
  const maxBounds = isArnhem ? arnhemMaxBounds : nijmegenMaxBounds;
  const zoom = 15;

  map = new mapboxgl.Map({
    accessToken: config.accessToken,
    container: config.mapId,
    style: 'mapbox://styles/dennislinku/ckzfemfoz005h15nz942jwdic',
    center,
    maxBounds,
    zoom,
  });

  map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));

  map.on('load', onMapLoad);
}

async function fetchHotspotCategories() {
  try {
    const response = await fetch(config.hotspotTypeEndpoint);

    if (response.status !== 200) {
      return [];
    }

    const json = await response.json();
    const mappedCategories = json.map((category) => ({
      key: `key-${category.id}`,
      label: category.name,
      color: category.acf.color || '#C3AF64',
    }));
    categories = mappedCategories;
    visiblePoints = Object.values(categories).map(({ key }) => key);
  } catch (error) {
    captureException(error);
    return [];
  }
}

async function fetchHotspotCities() {
  try {
    const response = await fetch(config.hotspotCityEndpoint);

    if (response.status !== 200) {
      return [];
    }

    const json = await response.json();
    const mappedCities = json.map((city) => ({
      key: `key-${city.id}`,
      label: city.name,
    }));

    cities = mappedCities;
  } catch (error) {
    captureException(error);
    return [];
  }
}

async function fetchHotspots() {
  try {
    const response = await fetch(config.hotspotEndpoint);

    if (response.status !== 200) {
      return [];
    }

    const hotspots = await response.json();

    const hotspotsToShow = hotspots
      .filter((hotspot) => hotspot.acf.show_on_map && !!hotspot.acf.address)
      .map((hotspot) => ({
        address: hotspot.acf.address,
        category: `key-${hotspot['hotspot-type'][0]}`,
        city: `key-${hotspot['hotspot-city'][0]}`,
        description: hotspot.acf.description,
        id: hotspot.id,
        image: hotspot.featured_image_url_webp,
        number: hotspot.acf.number,
        title: hotspot.title.rendered,
        website: hotspot.link,
      }))
      .filter(({ city }) => city === cityToUse.key);

    return hotspotsToShow;
  } catch (error) {
    captureException(error);
    return [];
  }
}

async function onMapLoad() {
  try {
    setupCategoriesFilter();
    loadIconsForMap(icons);

    const points = await fetchHotspots();

    const pointsWithCoordinates = await Promise.all(
      points.map(async (point) => ({
        ...point,
        category: categories.find((category) => point.category === category.key),
        coordinates: await getLngLatForQuery(point.address),
      }))
    );

    labels.forEach((label) => addLabelToMap(label));
    pointsWithCoordinates.forEach((point) => addPointToMap(point));
  } catch (error) {
    captureException(error);
  }
}

function addLabelToMap({ coordinates, label }) {
  const el = document.createElement('div');
  el.className = `map-label`;
  el.innerHTML = label;
  new mapboxgl.Marker(el).setLngLat(coordinates).addTo(map);
}

function addPointToMap({ category, coordinates, description, image, number, title, website }) {
  const el = document.createElement('div');
  el.className = `point ${category ? category.key : ''}`;
  el.style.backgroundColor = category ? category.color : '#C3AF64';
  el.innerHTML = number;

  if (!coordinates) {
    return;
  }

  new mapboxgl.Marker(el)
    .setLngLat(coordinates)
    .setPopup(
      new mapboxgl.Popup({ offset: 25 }).setHTML(
        `
          ${image ? `<img class="modal-image" src="${image}" alt="${title}"/>` : ''}
          ${category ? `<div class="modal-category">${category.label}</div>` : ''}
          ${title ? `<h3 class="modal-title">${title}</h3>` : ''}
          ${description ? `<p class="modal-description">${description}</p>` : ''}
          ${
            website
              ? `<a class="modal-link" href="${website}" target="_blank" rel="noopener noreferrer">Nieuwsgierig? Lees dan verder...</a>`
              : ''
          }
        `
      )
    )
    .addTo(map);
}

function loadIconsForMap(icons) {
  icons.forEach(({ coordinates, name, size, src }) => {
    map.loadImage(src, (error, image) => {
      if (error) {
        captureException(error);
      }

      const iconSourceName = `icon-source-${name}`;

      map.addImage(name, image);

      map.addSource(iconSourceName, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: coordinates } }],
        },
      });

      map.addLayer({
        id: iconSourceName,
        type: 'symbol',
        source: iconSourceName,
        layout: { 'icon-image': name, 'icon-size': size },
      });
    });
  });
}

async function getLngLatForQuery(query) {
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?limit=1&autocomplete=false&access_token=${config.accessToken}`
    ).then((r) => r.json());

    if (response?.features?.length === 0) {
      captureException(response);
      return undefined;
    }

    return response.features[0].center;
  } catch (error) {
    captureException(error);
    return undefined;
  }
}

function setupCategoriesFilter() {
  const containerEl = document.createElement('div');
  containerEl.className = 'categories-container';

  const logoEl = document.createElement('img');
  logoEl.src = logo;
  logoEl.className = 'categories-container-logo';
  containerEl.appendChild(logoEl);

  categories.forEach((category) => {
    const labelEl = document.createElement('label');
    labelEl.className = 'categories-container-input-container';

    const inputEl = document.createElement('input');
    inputEl.className = 'categories-container-input';
    inputEl.type = 'checkbox';
    inputEl.id = category.key;
    inputEl.checked = visiblePoints.includes(category.key);

    const spanEl = document.createElement('span');
    spanEl.className = 'categories-container-label';
    spanEl.innerHTML = category.label;

    const dotEl = document.createElement('div');
    dotEl.className = 'categories-container-dot';
    dotEl.style.backgroundColor = category.color;

    labelEl.appendChild(inputEl);
    labelEl.appendChild(dotEl);
    labelEl.appendChild(spanEl);

    inputEl.addEventListener('click', onInputElClick);

    containerEl.appendChild(labelEl);
  });

  const mapEl = document.getElementById(config.mapId);
  mapEl.appendChild(containerEl);
}

function onInputElClick(event) {
  if (event.target.checked) {
    visiblePoints = [...visiblePoints, event.target.id];
    const itemsThatNeedToBeVisible = [...document.querySelectorAll(`.${event.target.id}`)];
    itemsThatNeedToBeVisible.forEach((item) => {
      item.style.visibility = 'visible';
    });
  } else {
    visiblePoints = visiblePoints.filter((item) => item !== event.target.id);
    const itemsThatNeedToBeInvisible = [...document.querySelectorAll(`.${event.target.id}`)];
    itemsThatNeedToBeInvisible.forEach((item) => {
      item.style.visibility = 'hidden';
    });
  }
}

function captureException(error) {
  console.error(error);
}
